import React, { lazy, Suspense, useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "app/store/hook";
import { UserInfoModel } from "app/model/ua/user-info";
import { clearUserInfo } from "app/reducers/userSlice";
import { checkAiHealth, toggleChat } from "app/reducers/aiChatSlice";
import { Link, useNavigate } from "react-router-dom";
import AccountAlarm from "./AccountAlarm";

const AiChatPanel = lazy(() => import("./aichat/AiChatPanel"));
import AiChatHintBubble, {
  hasUserOpenedAiChat,
  markAiChatOpened,
  RESPONSE_READY_MESSAGE,
} from "./aichat/AiChatHintBubble";
import { AuthContextProps, useAuth } from "react-oidc-context";
import { Bot, LogOut, Settings } from "lucide-react";
import { startLogout } from "app/hooks/useSession";
import { UserInfoUtil } from "app/utils/userInfoUtil";
import { AuthConstant } from "app/constants/authConstant";

export default function Account() {
  const auth: AuthContextProps = useAuth();
  const [isUserLogin, setIsUserLogin] = useState<any>(false);
  const userInfo: UserInfoModel = useAppSelector(state => state.userInfo.userInfo);
  const isChatOpen = useAppSelector(state => state.aiChat.isOpen);
  const serviceAvailable = useAppSelector(state => state.aiChat.serviceAvailable);
  const healthChecking = useAppSelector(state => state.aiChat.healthChecking);
  const responseReadyNotice = useAppSelector(state => state.aiChat.responseReadyNotice);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [hasOpenedChat, setHasOpenedChat] = useState(true);
  // AI 챗봇은 산단기업 화면의 도우미다. 관리자와 장외거래사용자(산단기업이 아닌)에게는 보이지 않는다
  const chatAllowed = isUserLogin && !UserInfoUtil.isAdmin(userInfo)
    && !(UserInfoUtil.hasRole(userInfo, AuthConstant.R003) && !UserInfoUtil.hasRole(userInfo, AuthConstant.R001));

  useEffect(() => {
    if (userInfo?.userId) {
      setIsUserLogin(true);
      setHasOpenedChat(hasUserOpenedAiChat(userInfo.userId));
    } else {
      setIsUserLogin(false);
      setHasOpenedChat(true);
    }
  }, [userInfo?.userId]);

  useEffect(() => {
    if (!chatAllowed || hasOpenedChat || isChatOpen || !userInfo?.userId) {
      return;
    }
    dispatch(checkAiHealth());
    const intervalId = globalThis.setInterval(() => {
      dispatch(checkAiHealth());
    }, 30_000);
    return () => globalThis.clearInterval(intervalId);
  }, [dispatch, hasOpenedChat, isChatOpen, chatAllowed, userInfo?.userId]);

  const showWelcomeHint = !hasOpenedChat && !isChatOpen && serviceAvailable && !healthChecking;
  const showResponseNotice = responseReadyNotice && !isChatOpen;
  const showHintBubble = showWelcomeHint || showResponseNotice;

  const handleClickUser = (e: any) => {
    e.preventDefault();
    navigate('/mypage');
  }

  const handleLogout = async () => {
    localStorage.clear();
    dispatch(clearUserInfo());
    // 방식에 따라 절차가 다르다. BFF 는 게이트웨이 세션 하나만 끊으면 된다.
    await startLogout(auth);
  };

  const handleClickChatbot = (e: React.MouseEvent) => {
    e.preventDefault();
    if (userInfo?.userId) {
      markAiChatOpened(userInfo.userId);
      setHasOpenedChat(true);
    }
    dispatch(toggleChat());
  };

  return (
    <>
    <ul>
      {isUserLogin ? (
        <>
          {chatAllowed && <li className="chatbot">
            <AiChatHintBubble
              visible={showHintBubble}
              message={showResponseNotice ? RESPONSE_READY_MESSAGE : undefined}
            />
            <Link to="" onClick={handleClickChatbot} title="AI 챗봇">
              <Bot className="util-icon" aria-hidden="true" />
              <span className="hid">AI 챗봇</span>
            </Link>
          </li>}
          <li className="setting">
          <Link to="" onClick={handleClickUser} title="내 정보 화면으로 이동">
            <Settings className="util-icon" aria-hidden="true" />
            <span className="hid">내 정보</span>
          </Link>
        </li>
        </>
      ) : (
        <li className="login">
          <Link to="/" title="로그인 화면으로 이동"><span>로그인 해주세요</span></Link>
        </li>
      )}
      {
          isUserLogin && (<AccountAlarm/>)
      }
      {
        isUserLogin && (
          <>
            <li className="logout">
              <Link to="" onClick={handleLogout} title="로그아웃">
                <LogOut className="util-icon" aria-hidden="true" />
                <span className="hid">로그아웃</span>
              </Link>
            </li>
          </>
        )
      }
    </ul>
    {chatAllowed && (
      <Suspense fallback={null}>
        <AiChatPanel />
      </Suspense>
    )}
    </>
  )
}